import {
  getSavedSectionById,
  getSavedSectionsApi,
  injectSectionApi,
  saveSectionApi,
} from 'api/savedSectionsApi';
import { cloneDeep, isArray, uniqBy } from 'lodash';
import {
  TemplateType,
  IChecklistMetaData,
  IChecklistSection,
  ChecklistItemType,
} from 'pages/TemplateDashboard/context';
import { STEP_TYPES } from 'utils/templates';
import { omit } from 'lodash';

// The purpose of this hook is to accomplish functionalities which can be commonly consumed for building template
const useTemplateBuilder = () => {
  // Convert to Initial Steps when creating template
  const convertInitialSteps = (
    checklist: TemplateType,
    metadata: IChecklistMetaData,
  ) => {
    const checklistSections: any = [];
    for (let i = 0; i < checklist?.ChecklistItems?.length; i++) {
      const item = checklist?.ChecklistItems?.[i];
      if (item?.type === STEP_TYPES.HEADER) {
        checklistSections.push({
          ...omit(item, 'type'),
          checklistItems: [],
        });
      } else {
        checklistSections[checklistSections?.length - 1]?.checklistItems?.push(
          item,
        );
      }
    }

    return {
      checklistSections,
      ...(metadata ?? {}),
      // name: checklist?.name,
      // icon: checklist?.icon,
    };
  };

  // This is for the edge case where we already have the same order for two items
  const updateOrderIfSame = (
    prevItem: TemplateType,
    nextItem: TemplateType,
  ) => {
    const tempPrevItem = prevItem;
    if (tempPrevItem?.order === nextItem?.order) {
      tempPrevItem.order = tempPrevItem?.order - 0.0000001;
      tempPrevItem.isDirty = true;
    }

    return {
      lastOrder: tempPrevItem?.order,
      nextOrder: nextItem?.order,
      anotherItemToUpdate: tempPrevItem,
    };
  };

  // Update Checklist Items State as soon as we drag and drop a checklist item.
  const checklistUpdaterAfterDragDrop = (
    checklistItems: TemplateType[],
    checklistItemsHashRef: Record<string, TemplateType>,
    activeItem: TemplateType,
    updatedActiveItem: {
      order?: any;
      isDirty?: boolean;
      ParentSectionId?: string | undefined | null;
    },
    updateChecklistItemsHash: (id: string, item) => void,
    itemToUpdate: TemplateType,
    handleUpdateChecklistItems: (
      parentId: string | undefined | null,
      checklistItems: TemplateType[],
      isSection: boolean,
      isActive: boolean,
      isDirty: boolean,
      isDeleted: boolean,
      isFollowupSectionDragged?: any,
    ) => void,
    parentId: string | null | undefined,
    updateItemOrdersHash: (items: TemplateType[]) => void,
  ) => {
    // Update items with new ordering
    const finalChecklistItems = checklistItems?.map((item) => {
      // If this is the moved item
      if (item?.id === activeItem?.id) {
        const transformedItem = {
          ...(checklistItemsHashRef[item.id] ?? item),
          ...(updatedActiveItem ?? {}),
        };

        updateChecklistItemsHash(item?.id, transformedItem);
        return transformedItem;
      }

      let updatingItem = itemToUpdate;

      if (isArray(itemToUpdate)) {
        updatingItem = itemToUpdate?.find((i) => i?.id == item?.id);
      }

      if (updatingItem?.id == item?.id) {
        const transformedItem = {
          ...(checklistItemsHashRef[item.id] ?? item),
          ...(updatingItem ?? {}),
        };
        checklistItemsHashRef[item.id] = transformedItem;
        return transformedItem;
      }
      // All other items remain unchanged
      return checklistItemsHashRef[item.id] ?? item;
    });

    // Sort and update
    let sortedItems = convertChecklistToOrderedList(finalChecklistItems);
    updateItemOrdersHash?.(sortedItems);

    const isFollowupSectionDragged =
      activeItem?.type === STEP_TYPES.HEADER && activeItem?.ParentId;
    if (isFollowupSectionDragged) {
      const updatedItems: any = [];

      sortedItems?.forEach((item) => {
        const isExists = updatedItems?.find((i) => i?.id === item?.id);
        if (!isExists) {
          updatedItems.push(item);
        }
        item?.ChecklistItems?.forEach((child) => {
          updatedItems.push(child);
        });
      });

      sortedItems = updatedItems;
    }

    handleUpdateChecklistItems(
      parentId,
      sortedItems,
      !!parentId,
      false,
      false,
      false,
      isFollowupSectionDragged,
    );
  };

  // Covert Checklist Items into proper order (recursive)
  const convertChecklistToOrderedList = (checklistItems) => {
    if (!checklistItems?.length) return [];

    // Create a single sorted array of non-header items without sections
    const standaloneItems = checklistItems
      ?.filter(
        (item) => item.type !== STEP_TYPES.HEADER && !item.ParentSectionId,
      )
      ?.sort((a, b) => a.order - b.order);

    // Helper function to recursively process items at a given level
    const processItemsRecursively = (
      items: TemplateType[],
      parentSectionId: string | null | undefined = null,
    ): TemplateType[] => {
      const result: TemplateType[] = [];

      // First, add all non-header items at this level
      const nonHeaderItems = items
        ?.filter(
          (item) =>
            item.type !== STEP_TYPES.HEADER &&
            item.ParentSectionId === parentSectionId,
        )
        ?.sort((a, b) => a.order - b.order);

      result.push(...nonHeaderItems);

      // Then, add headers and recursively process their children
      const headerItems = items
        ?.filter(
          (item) =>
            item.type === STEP_TYPES.HEADER &&
            item.ParentSectionId === parentSectionId,
        )
        ?.sort((a, b) => a.order - b.order);

      headerItems?.forEach((header) => {
        // Add the header itself
        result.push(header);

        // Recursively process all children of this header
        result.push(...processItemsRecursively(items, header.id));
      });

      return result;
    };

    // Process only headers and their nested items recursively
    const rootHeaders = checklistItems
      ?.filter(
        (item) => item.type === STEP_TYPES.HEADER && !item.ParentSectionId,
      )
      ?.sort((a, b) => a.order - b.order);

    const headerItemsMap =
      rootHeaders?.reduce((acc: TemplateType[], header) => {
        // Add the header itself
        acc.push(header);

        // Recursively process all children of this header
        acc.push(...processItemsRecursively(checklistItems, header.id));

        return acc;
      }, []) || [];

    // Combine standalone items with header sections
    return [...standaloneItems, ...headerItemsMap];
  };

  const renameFollowUpsRecursively = (item: any): any => {
    const newItem = { ...item };

    if (newItem.followUps && Array.isArray(newItem.followUps)) {
      newItem.ChildItems = newItem.followUps.map((child) =>
        renameFollowUpsRecursively(child),
      );
    }

    delete newItem.followUps;
    return newItem;
  };

  const hierarchyToLinear = (data: any[], flatFollowUps = true) => {
    const allItems: any[] = [];

    // Recursive processor with parent tracking
    const processItem = (
      item: any,
      parentSectionId: any = null,
      noInjectParentSectionId = false,
    ) => {
      const flatItem = { ...item };
      delete flatItem.checklistItems;
      delete flatItem.subSections;

      if (!flatFollowUps) {
        const followUps = cloneDeep(flatItem?.followUps ?? []);
        flatItem.ChildItems = followUps?.map((child) => {
          return renameFollowUpsRecursively(child);
        });
      }

      delete flatItem.followUps;

      if (!noInjectParentSectionId) {
        flatItem.ParentSectionId = parentSectionId;
      }

      allItems.push(flatItem);

      if (item.ChecklistItems && Array.isArray(item.ChecklistItems)) {
        item.ChecklistItems.forEach((child) => processItem(child, item.id));
      }

      if (item.TaskChecklistItems && Array.isArray(item.TaskChecklistItems)) {
        item.TaskChecklistItems.forEach((child) => processItem(child, item.id));
      }

      if (flatFollowUps) {
        if (item.followUps && Array.isArray(item.followUps)) {
          item.followUps
            .filter((item) => item?.ParentId)
            ?.forEach((child) => processItem(child, item.id, true));
        }
      }

      if (item.subSections && Array.isArray(item.subSections)) {
        item.subSections.forEach((child) => processItem(child, item.id));
      }
    };

    data.forEach((item) => processItem(item));

    return allItems;
  };

  const insertSubSections = (
    parentSections,
    allSections,
    checklistProperty,
    sectionsProperty,
  ) => {
    return parentSections?.map((section) => {
      // Find direct children of this section
      const directSubSections = allSections?.filter(
        (s) => s?.ParentSectionId === section?.id,
      );

      // Recursively build each subsection
      const nestedSubSections = insertSubSections(
        directSubSections,
        allSections,
        checklistProperty,
        sectionsProperty,
      );

      const checklistItems = section?.[checklistProperty];

      const rootItems = checklistItems
        ?.filter((item) => !item?.ParentId)
        .sort((a, b) => a.order - b.order);

      const followUpChecklistItems = checklistItems
        ?.filter((item) => item?.ParentId)
        .sort((a, b) => a.order - b.order);

      const followUpChecklistSections = allSections
        ?.filter((item) => item?.ParentId)
        .sort((a, b) => a.order - b.order)
        ?.map((item) => ({
          ...item,
          type: STEP_TYPES.HEADER,
        }));

      const followUpItems = [
        ...(followUpChecklistItems ?? []),
        ...(followUpChecklistSections ?? []),
      ];

      let updatedRootItems = insertFollowupItemsInItems(
        rootItems,
        followUpItems,
      );

      updatedRootItems = updatedRootItems?.map((item) => {
        const processFollowUps = (followUpItem) => {
          const allFollowups: any[] = [];

          followUpItem?.followUps?.forEach((child) => {
            if (child?.type === STEP_TYPES.HEADER) {
              const transformedSection = {
                ...child,
                ParentId: null,
                RelatedAutomationId: null,
                ParentSectionId: null,
              };
              let sectionLinear = organizeChecklistIntoLinear({
                ChecklistSections: [transformedSection],
                ChecklistItems: [],
              });

              sectionLinear = sectionLinear.map((item) => {
                if (item?.id === child?.id) {
                  return child;
                }
                return item;
              });
              sectionLinear?.forEach((item) => {
                allFollowups.push(item);
              });
            } else {
              // Process nested followUps recursively
              const processedChild = {
                ...child,
                followUps: processFollowUps(child),
              };
              allFollowups.push(processedChild);
            }
          });

          // Add items whose ParentSectionId matches child item's ID
          followUpItem?.followUps?.forEach((child) => {
            const itemsWithMatchingParentSection = followUpItems?.filter(
              (item) => item?.ParentSectionId === child?.id,
            );
            if (itemsWithMatchingParentSection?.length > 0) {
              allFollowups.push(...itemsWithMatchingParentSection);
            }
          });

          return allFollowups;
        };

        return {
          ...item,
          followUps: processFollowUps(item),
        };
      });

      return {
        ...section,
        isActive: section?.isActive ?? true,
        type: STEP_TYPES.HEADER,
        [checklistProperty]: updatedRootItems,
        subSections: nestedSubSections,
      };
    });
  };

  //Organize Checklist Items and Sections
  const organizeChecklistIntoLinear = (data, flatFollowUps = true) => {
    const updated = organizeChecklistIntoHierarchy(data);
    const allItems = hierarchyToLinear(updated, flatFollowUps);
    return allItems;
  };

  const organizeUnStructuredChecklistIntoLinear = (
    data,
    flatFollowUps = true,
    removeParentSectionsFiltering = true,
  ) => {
    const updated = organizeChecklistIntoHierarchy(
      data,
      removeParentSectionsFiltering,
    );
    const allItems = hierarchyToLinear(updated, flatFollowUps);
    return uniqBy(allItems, 'id');
  };

  const insertFollowupItemsInItems = (rootItems, followupItems) => {
    return rootItems?.map((rootItem) => {
      // Get immediate followups for this root item
      const immediateFollowups = followupItems?.filter(
        (item) =>
          item?.ParentId === rootItem?.ChecklistItemId ||
          item?.ParentId === rootItem?.id ||
          item?.ParentId === rootItem?.ChecklistSectionId,
      );

      // Recursively process each followup to get their followups
      const processedFollowups = immediateFollowups?.map((followup) => {
        // Get followups for this followup item
        const nestedFollowups = followupItems?.filter(
          (item) =>
            item?.ParentId === followup?.ChecklistItemId ||
            item?.ParentId === followup?.id ||
            item?.ParentId === followup?.ChecklistSectionId,
        );

        return {
          ...followup,
          followUps: insertFollowupItemsInItems(nestedFollowups, followupItems),
        };
      });

      return {
        ...rootItem,
        followUps: processedFollowups,
      };
    });
  };

  // Organize Checklist Items and Sections for Submission
  const organizeChecklistIntoHierarchy = (
    data,
    removeParentSectionsFiltering = false,
  ) => {
    let sectionsProperty = 'TaskChecklistSections';
    if (data?.ChecklistSections) {
      sectionsProperty = 'ChecklistSections';
    }

    let checklistProperty = 'TaskChecklistItems';
    if (data?.ChecklistItems) {
      checklistProperty = 'ChecklistItems';
    }
    const checklist = {
      [checklistProperty]: data?.[checklistProperty],
      [sectionsProperty]: data?.[sectionsProperty],
    };

    const rootItems = checklist?.[checklistProperty]
      ?.filter((item) => !item?.ParentId)
      .sort((a, b) => a.order - b.order);

    const followUpChecklistItems = checklist?.[checklistProperty]?.filter(
      (item) => item?.ParentId,
    );

    const followUpChecklistSections = checklist?.[sectionsProperty]
      ?.filter((item) => item?.ParentId)
      ?.map((item) => ({
        ...item,
        type: STEP_TYPES.HEADER,
      }));

    const followUpItems = [
      ...(followUpChecklistItems ?? []),
      ...(followUpChecklistSections ?? []),
    ].sort((a, b) => a.order - b.order);

    let updatedRootItems = insertFollowupItemsInItems(rootItems, followUpItems);

    updatedRootItems = updatedRootItems?.map((item) => {
      const processFollowUps = (followUpItem) => {
        const allFollowups: any[] = [];

        followUpItem?.followUps?.forEach((child) => {
          if (child?.type === STEP_TYPES.HEADER) {
            const transformedSection = {
              ...child,
              ParentId: null,
              RelatedAutomationId: null,
              ParentSectionId: null,
            };
            let sectionLinear = organizeChecklistIntoLinear({
              ChecklistSections: [transformedSection],
              ChecklistItems: [],
            });

            sectionLinear = sectionLinear.map((item) => {
              if (item?.id === child?.id) {
                return child;
              }
              return item;
            });
            sectionLinear?.forEach((item) => {
              allFollowups.push(item);
            });
          } else {
            // Process nested followUps recursively
            const processedChild = {
              ...child,
              followUps: processFollowUps(child),
            };
            allFollowups.push(processedChild);
          }
        });

        // Add items whose ParentSectionId matches child item's ID
        followUpItem?.followUps?.forEach((child) => {
          const itemsWithMatchingParentSection = followUpItems?.filter(
            (item) => item?.ParentSectionId === child?.id,
          );
          if (itemsWithMatchingParentSection?.length > 0) {
            allFollowups.push(...itemsWithMatchingParentSection);
          }
        });

        return allFollowups;
      };

      return {
        ...item,
        followUps: processFollowUps(item),
      };
    });

    const parentSections = removeParentSectionsFiltering
      ? checklist?.[sectionsProperty]
      : checklist?.[sectionsProperty]?.filter(
          (section) => !section?.ParentSectionId && !section?.ParentId,
        );

    let updatedSections = insertSubSections(
      parentSections,
      checklist?.[sectionsProperty],
      checklistProperty,
      sectionsProperty,
    );
    updatedSections = updatedSections
      ?.map((section) => {
        return {
          ...section,
          isActive: section?.isActive ?? true,
          type: STEP_TYPES.HEADER,
        };
      })
      ?.sort((a, b) => a.order - b.order);

    return [...(updatedRootItems ?? []), ...(updatedSections ?? [])];
  };

  const saveSectionAction = async (
    sectionId: string,
    sectionName: string,
    payload,
  ) => {
    const response = await saveSectionApi(sectionId, sectionName, payload);
    return response?.data;
  };

  const getSavedSections = async () => {
    const response = await getSavedSectionsApi();
    return response?.data;
  };

  const injectSectionInTemplate = async (payload) => {
    const response = await injectSectionApi(payload);
    return response;
  };

  const getSavedSection = async (payload) => {
    const response = await getSavedSectionById(payload);
    return response;
  };

  // BUILDER CHECKLIST ORGANIZE - PROCESS =========

  const findChildrenRecursivelyBuilder = (
    allItems: (ChecklistItemType | IChecklistSection)[],
    parentId: string | undefined,
    isRoot = false,
  ): any[] => {
    const result: (ChecklistItemType | IChecklistSection)[] = [];

    // Find immediate children
    let children: (ChecklistItemType | IChecklistSection)[] = [];

    if (isRoot) {
      children = allItems?.filter(
        (item) =>
          item?.ParentId === parentId &&
          item?.type !== STEP_TYPES.HEADER &&
          !item?.ParentSectionId,
      );
    } else {
      children = allItems?.filter(
        (item) =>
          (item?.ParentId === parentId || item?.ParentSectionId === parentId) &&
          item?.type !== STEP_TYPES.HEADER,
      );
    }

    // For each child, recursively find its children
    children?.forEach((child: ChecklistItemType | IChecklistSection) => {
      // If isRootOfSection is true, remove ParentId and RelatedAutomationId
      const processedChild = child?.isRootOfSection
        ? { ...child, ParentId: null, RelatedAutomationId: null }
        : child;

      result.push(processedChild);
      const grandChildren = findChildrenRecursivelyBuilder(
        allItems,
        child?.id,
        isRoot,
      );
      result.push(...(grandChildren ?? []));
    });

    return result;
  };

  const getChecklistItemsBuilder = (
    allItems: (ChecklistItemType | IChecklistSection)[],
    rootItems: (ChecklistItemType | IChecklistSection)[],
    isRoot = false,
  ) => {
    const checklistItems: (ChecklistItemType | IChecklistSection)[] = [];
    rootItems?.forEach((rootItem) => {
      const processedRootItem = rootItem?.isRootOfSection
        ? { ...rootItem, ParentId: null, RelatedAutomationId: null }
        : rootItem;
      checklistItems.push(processedRootItem);

      const children = findChildrenRecursivelyBuilder(
        allItems,
        rootItem?.id,
        isRoot,
      );

      checklistItems.push(...(children ?? []));
    });

    return checklistItems;
  };

  const processDirtyItemsBuilder = (
    allItems: (ChecklistItemType | IChecklistSection)[] = [],
    checklistItemsHashRef: any,
  ) => {
    let clonedAllItems = cloneDeep(allItems);
    // Checklist Items
    const rootItems = clonedAllItems?.filter(
      (item) =>
        item?.type !== STEP_TYPES.HEADER &&
        !item?.ParentSectionId &&
        !item?.ParentId &&
        !item?.RelatedAutomationId,
    );
    const checklistItems = getChecklistItemsBuilder(
      clonedAllItems,
      rootItems,
      true,
    );

    // Sections
    let checklistSections: any = clonedAllItems?.filter(
      (item) => item?.type === STEP_TYPES.HEADER,
    );

    clonedAllItems = clonedAllItems?.map((item) => {
      const section = checklistSections?.find(
        (s) => s?.id === item?.ParentSectionId,
      );

      if (section) {
        return {
          ...item,
          ParentId: null,
          RelatedAutomationId: null,
        };
      }

      return item;
    });

    checklistSections = checklistSections?.map((section) => {
      const rootItems = clonedAllItems
        ?.filter(
          (item) =>
            item?.type !== STEP_TYPES.HEADER &&
            item?.ParentSectionId === section?.id,
        )
        ?.map((item) => {
          return {
            ...item,
            isRootOfSection: true,
          };
        });

      const checklistItems = getChecklistItemsBuilder(
        clonedAllItems,
        rootItems,
      );
      return {
        ...section,
        checklistItems,
      };
    });

    const dirtyItems = clonedAllItems;

    dirtyItems?.forEach((item) => {
      const clonedItem = checklistItemsHashRef?.current[item?.id];
      delete clonedItem?.isDirty;
      checklistItemsHashRef.current[item?.id] = clonedItem;
    });

    return {
      checklistItems: checklistItems?.filter((item) => item?.isDirty),
      checklistSections: checklistSections?.filter(
        (item) =>
          item?.isDirty || item?.checklistItems?.some((item) => item?.isDirty),
      ),
    };
  };

  // SUBMISSION CHECKLIST ORGANIZE =========

  const findChildrenRecursively = (
    allItems: (ChecklistItemType | IChecklistSection)[],
    parentId: string | undefined,
    isRoot = false,
  ): any[] => {
    const result: (ChecklistItemType | IChecklistSection)[] = [];

    // Find immediate children
    let children: (ChecklistItemType | IChecklistSection)[] = [];

    children = allItems?.filter(
      (item) =>
        item?.ParentId === parentId || item?.ParentSectionId === parentId,
    );

    children?.sort((a, b) => a.order - b.order);

    // For each child, recursively find its children
    children?.forEach((child: ChecklistItemType | IChecklistSection) => {
      const processedChild = child;

      result.push(processedChild);

      const grandChildren = findChildrenRecursively(
        allItems,
        child?.ChecklistItemId || child?.ChecklistSectionId,
        isRoot,
      );

      result.push(...(grandChildren ?? []));
    });

    return result;
  };

  const convertIntoLinearArray = (items, sections) => {
    const allItems: (ChecklistItemType | IChecklistSection)[] = [];

    items?.forEach((item) => {
      allItems.push(item);
    });

    sections?.forEach((parentItem) => {
      allItems.push({
        ...parentItem,
        type: STEP_TYPES.HEADER,
      });

      const sortedItems = parentItem?.TaskChecklistItems?.sort(
        (a, b) => a.order - b.order,
      );
      sortedItems?.forEach((item) => {
        allItems?.push({
          ...item,
          ...(!item?.ParentId && {
            ParentSectionId: parentItem?.ChecklistSectionId,
          }),
          sectionId: parentItem?.ChecklistSectionId,
        });
      });
    });

    return allItems;
  };

  return {
    convertInitialSteps,
    updateOrderIfSame,
    convertChecklistToOrderedList,
    checklistUpdaterAfterDragDrop,
    saveSectionAction,
    getSavedSections,
    injectSectionInTemplate,
    getSavedSection,
    organizeChecklistIntoLinear,
    organizeChecklistIntoHierarchy,
    processDirtyItemsBuilder,
    organizeUnStructuredChecklistIntoLinear,
  };
};

export default useTemplateBuilder;
